import React, { useState, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { PatientHistoryType } from 'src/graphql.generated'
import { Card, CardContent, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material'
import { PatientHistoryFormType, PATIENT_HISTORY_TYPE_NAMES } from 'src/participantHistory/constants'
import Form from './form/Form'
import { getFormFromLocalStorage } from './form/persistFormHelpers'
import { useGetPatientHistoryTypes } from './useGetPatientHistoryTypes'

const ParticipantHistoryForm = ({ uid }: { uid: string }) => {
  const patientHistoryTypesResult = useGetPatientHistoryTypes(uid)
  const patientHistoryTypes = useMemo(
    () => (patientHistoryTypesResult || []) as PatientHistoryFormType[],
    [patientHistoryTypesResult]
  )
  const [formType, setFormType] = useState<PatientHistoryFormType>(() => getDefaultFormType(uid))
  const location = useLocation()

  // Check for formType in URL parameters and update if present
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const urlFormType = searchParams.get('formType')

    if (urlFormType && patientHistoryTypes.includes(urlFormType as PatientHistoryFormType)) {
      setFormType(urlFormType as PatientHistoryFormType)
    }
  }, [location.search, patientHistoryTypes])

  const handleTypeChange = (event: SelectChangeEvent<PatientHistoryType | 'FINANCIAL_ELIGIBILITY_HEALTH_PLAN'>) => {
    const type = event.target.value
    setFormType(type as PatientHistoryFormType)
  }

  return (
    <Card>
      <CardContent>
        <FormControl>
          <InputLabel id="participant-history-select-label">Type</InputLabel>
          <Select
            labelId="participant-history-select-label"
            value={formType}
            label="Type"
            onChange={handleTypeChange}
            size="small"
          >
            {patientHistoryTypes.map((field) => (
              <MenuItem key={field} value={field}>
                {PATIENT_HISTORY_TYPE_NAMES[field]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Form uid={uid} formType={formType} setFormType={setFormType} />
      </CardContent>
    </Card>
  )
}

const getDefaultFormType = (uid: string) => {
  const intakeNoteSavedInLocalStorage = getFormFromLocalStorage(uid, PatientHistoryType.IntakeNote)
  if (intakeNoteSavedInLocalStorage) {
    return PatientHistoryType.IntakeNote
  }

  const intakeNotePsychiatrySavedInLocalStorage = getFormFromLocalStorage(uid, PatientHistoryType.IntakeNotePsychiatry)

  if (intakeNotePsychiatrySavedInLocalStorage) {
    return PatientHistoryType.IntakeNotePsychiatry
  }

  const intakeNoteComprehensiveSavedInLocalStorage = getFormFromLocalStorage(
    uid,
    PatientHistoryType.IntakeNoteComprehensive
  )
  if (intakeNoteComprehensiveSavedInLocalStorage) {
    return PatientHistoryType.IntakeNoteComprehensive
  }

  return PatientHistoryType.Note
}

export default ParticipantHistoryForm
